import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import useSeo from "@/hooks/useSeo";
import { gossipEvents } from "@/mocks/events";
import { siteInfo } from "@/mocks/site";
import Breadcrumb from "@/components/feature/Breadcrumb";
import EventCard from "@/components/feature/EventCard";
import EventTimeline from "@/pages/event-detail/components/EventTimeline";
import Reveal from "@/components/base/Reveal";
import { formatDate, formatDateFull, formatHeat, statusColor } from "@/lib/format";

const sections = [
  { id: "overview", label: "事件概览" },
  { id: "timeline", label: "事件时间线" },
  { id: "analysis", label: "追瓜笔记" },
  { id: "related", label: "相关事件" },
];

export default function EventDetail() {
  const { id } = useParams();
  const event = gossipEvents.find((item) => String(item.id) === id);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      const total = document.documentElement.scrollHeight - window.innerHeight;
      const value = total > 0 ? (window.scrollY / total) * 100 : 0;
      setProgress(Math.min(100, Math.max(0, value)));
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [id]);

  useSeo({
    title: event ? `${event.title} - 网报吃瓜事件串` : "事件未找到 - 网报吃瓜",
    description: event
      ? `${event.summary}网报吃瓜已把该事件按发生、发酵、回应、后续整理成完整事件串，收录于${event.category}分类。`
      : "该事件串暂未收录，可在网报吃瓜事件库中浏览其他热门话题。",
    keywords: event
      ? `${event.category},${event.tags.join(",")},网报吃瓜,网报吃瓜整理,吃瓜事件串`
      : "网报吃瓜,吃瓜事件库",
    canonical: event ? `https://zhongjianshuyuan.cn/events/${event.id}` : "https://zhongjianshuyuan.cn/events",
    jsonLd:
      event &&
      [
        {
          "@context": "https://schema.org",
          "@type": "Article",
          headline: event.title,
          description: event.summary,
          articleSection: event.category,
          keywords: event.tags.join(","),
          datePublished: event.publishedAt,
          dateModified: event.updatedAt,
          inLanguage: "zh-CN",
          author: { "@type": "Organization", name: siteInfo.name },
          publisher: { "@type": "Organization", name: siteInfo.name },
          mainEntityOfPage: `https://zhongjianshuyuan.cn/events/${event.id}`,
        },
        {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "网报吃瓜", item: "https://zhongjianshuyuan.cn/" },
            { "@type": "ListItem", position: 2, name: "吃瓜事件库", item: "https://zhongjianshuyuan.cn/events" },
            { "@type": "ListItem", position: 3, name: event.title, item: `https://zhongjianshuyuan.cn/events/${event.id}` },
          ],
        },
      ],
  });

  if (!event) {
    return (
      <section className="flex min-h-screen flex-col items-center justify-center px-4 pt-24 text-center">
        <i className="ri-emotion-sad-line text-5xl text-foreground-400" />
        <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">这条事件串还没被收录</h1>
        <p className="mt-3 max-w-md text-sm text-foreground-600">
          可能链接有误，或者这条瓜已经在整理队列里。先去事件库看看别的吧。
        </p>
        <Link
          to="/events"
          className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
        >
          <i className="ri-list-check-2" />
          返回吃瓜事件库
        </Link>
      </section>
    );
  }

  const related = event.related
    .map((rid) => gossipEvents.find((item) => item.id === rid))
    .filter((item): item is (typeof gossipEvents)[number] => Boolean(item));

  return (
    <>
      <div className="fixed left-0 top-0 z-[60] h-1 bg-primary-500 transition-[width] duration-150" style={{ width: `${progress}%` }} />

      <section className="w-full bg-background-100 px-4 pb-8 pt-28 md:px-8 md:pt-36">
        <Breadcrumb
          items={[
            { label: "网报吃瓜", to: "/" },
            { label: "吃瓜事件库", to: "/events" },
            { label: event.category, to: `/events?category=${encodeURIComponent(event.category)}` },
            { label: event.title },
          ]}
        />

        <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <div className="flex flex-wrap items-center gap-2">
              <span className={`rounded-full px-3 py-1 text-xs font-medium ${statusColor(event.status)}`}>{event.status}</span>
              <span className="rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-800">{event.category}</span>
              {event.tags.map((tag) => (
                <span key={tag} className="rounded-full border border-background-300 px-3 py-1 text-xs text-foreground-600">
                  #{tag}
                </span>
              ))}
            </div>

            <h1 className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-4xl">
              {event.title}
            </h1>

            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-fire-line text-primary-500" />
                站内热度 <strong className="font-semibold text-primary-600">{formatHeat(event.heat)}</strong>
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line" />
                {event.duration}内整理
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line" />
                首次收录 {formatDateFull(event.publishedAt)}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-refresh-line" />
                最近整理 {formatDate(event.updatedAt)}
              </span>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full px-4 pb-16 md:px-8">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
          <article className="lg:col-span-8">
            <div className="overflow-hidden rounded-lg border border-background-200">
              <img
                src={event.cover}
                alt={`${event.title}事件脉络配图`}
                title={`${event.title} - 网报吃瓜事件串`}
                className="h-56 w-full object-cover object-top md:h-80"
              />
            </div>

            <section id="overview" className="mt-8 scroll-mt-24">
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">事件概览</h2>
              <p className="mt-3 rounded-lg border-l-4 border-primary-400 bg-primary-50 p-4 text-sm leading-relaxed text-foreground-700">
                {event.summary}
              </p>
            </section>

            <div className="mt-8">
              <EventTimeline items={event.timeline} />
            </div>

            <section id="analysis" className="mt-10 scroll-mt-24">
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">追瓜笔记</h2>
              <div className="mt-4 flex flex-col gap-4">
                {event.paragraphs.map((paragraph, index) => (
                  <p key={paragraph.slice(0, 12)} className="text-sm leading-7 text-foreground-700 md:text-[15px]">
                    <span className="mr-2 font-heading text-sm font-bold text-primary-500">0{index + 1}</span>
                    {paragraph}
                  </p>
                ))}
              </div>
            </section>

            <div className="mt-8 rounded-lg border border-secondary-200 bg-secondary-50 p-5">
              <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <i className="ri-information-line text-secondary-600" />
                阅读提示
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                本条事件串基于公开信息整理，热度为站内相对参考值，不代表真实统计口径。涉及尚未有明确回应的内容，请以当事方官方发布为准。
              </p>
            </div>

            <section id="related" className="mt-12 scroll-mt-24">
              <div className="flex items-end justify-between">
                <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">相关事件推荐</h2>
                <Link to="/events" className="text-sm text-primary-600 hover:text-primary-700 whitespace-nowrap">
                  更多事件
                </Link>
              </div>
              <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2">
                {related.map((item, index) => (
                  <Reveal key={item.id} delay={index * 70} className="h-full">
                    <EventCard event={item} />
                  </Reveal>
                ))}
              </div>
            </section>
          </article>

          <aside className="lg:col-span-4">
            <div className="sticky top-24 flex flex-col gap-5">
              <nav aria-label="本页目录" className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h2 className="font-heading text-sm font-bold text-foreground-950">本页目录</h2>
                <ul className="mt-3 flex flex-col gap-1">
                  {sections.map((section) => (
                    <li key={section.id}>
                      <a
                        href={`#${section.id}`}
                        className="flex items-center gap-2 rounded-md px-2.5 py-2 text-sm text-foreground-600 transition-colors hover:bg-primary-50 hover:text-primary-700"
                      >
                        <i className="ri-subtract-line text-primary-400" />
                        {section.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </nav>

              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h2 className="font-heading text-sm font-bold text-foreground-950">事件卡片</h2>
                <dl className="mt-3 flex flex-col gap-3 text-sm">
                  <div className="flex items-center justify-between gap-3">
                    <dt className="text-foreground-500">分类方向</dt>
                    <dd className="font-medium text-foreground-800">{event.category}</dd>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <dt className="text-foreground-500">当前状态</dt>
                    <dd className="font-medium text-foreground-800">{event.status}</dd>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <dt className="text-foreground-500">记录节点</dt>
                    <dd className="font-medium text-foreground-800">{event.timeline.length} 个</dd>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <dt className="text-foreground-500">整理跨度</dt>
                    <dd className="font-medium text-foreground-800">{event.duration}</dd>
                  </div>
                </dl>
              </div>

              <div className="rounded-lg bg-accent-950 p-5">
                <h2 className="flex items-center gap-2 font-heading text-sm font-bold text-white">
                  <i className="ri-mail-send-line text-secondary-300" />
                  想第一时间看到更新？
                </h2>
                <p className="mt-2 text-sm leading-relaxed text-background-200">
                  订阅每周汇总，新节点整理好会一起发给你。
                </p>
                <Link
                  to="/#subscribe"
                  className="mt-4 inline-flex items-center gap-2 rounded-full bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
                >
                  订阅汇总
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}